import { useMutation, useQuery } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { GET_SALON_STOREFRONT, UPDATE_SALON_STOREFRONT } from "@/graphql/salon";
import { useSalonStore } from "@/lib/store/salon";
import { RESET_OPTIONS } from "./reset-options";

const announcementSchema = z.object({
  announcementText: z.string(),
});

export type AnnouncementFormData = z.infer<typeof announcementSchema>;

export function useAnnouncementText() {
  const { currentSalon } = useSalonStore();
  const salonId = currentSalon?.id ?? "";
  const [saving, setSaving] = useState(false);

  const { data, loading, error } = useQuery(GET_SALON_STOREFRONT, {
    variables: { salonId },
    skip: !salonId,
    fetchPolicy: "cache-and-network",
  });

  const [updateSalon] = useMutation(UPDATE_SALON_STOREFRONT);

  const form = useForm<AnnouncementFormData>({
    resolver: zodResolver(announcementSchema),
    defaultValues: { announcementText: "" },
  });

  // Seed once per salon: later refetches must not overwrite what the user is
  // typing, but switching salons (no remount) must load the new salon's value.
  const seededSalonId = useRef<string | null>(null);
  // Last value known to be stored on the server.
  const savedText = useRef("");
  const salon = data?.getSalon;
  useEffect(() => {
    if (salon && salon.id === salonId && seededSalonId.current !== salonId) {
      savedText.current = salon.announcementText ?? "";
      form.reset({ announcementText: savedText.current }, RESET_OPTIONS);
      seededSalonId.current = salonId;
    }
  }, [salon, salonId, form]);

  const save = useCallback(async () => {
    const values = form.getValues();
    // Diff against the server value instead of reading `form.formState.isDirty`,
    // which React Compiler can memoize away inside this callback.
    if (values.announcementText === savedText.current) return;

    setSaving(true);
    try {
      await updateSalon({
        variables: {
          salonId,
          // The backend skips `undefined` as "unchanged", so clearing must send null.
          input: { announcementText: values.announcementText || null },
        },
      });
      savedText.current = values.announcementText;
      form.reset(values, RESET_OPTIONS);
      toast.success("Announcement saved");
    } catch (err) {
      console.error("Save announcement failed", err);
      toast.error(err instanceof Error ? err.message : "Failed to save announcement");
      throw err;
    } finally {
      setSaving(false);
    }
  }, [form, salonId, updateSalon]);

  const discard = useCallback(() => {
    form.reset(undefined, RESET_OPTIONS);
  }, [form]);

  // `cache-and-network` keeps `loading` true during background refetches even
  // when data is already shown; only block input until the form is seeded.
  const initialLoading = loading && salon?.id !== salonId;

  return { form, loading: initialLoading, saving, error, save, discard };
}
